import { FC, useEffect, useState } from "react";
import { Form, Input, Spin } from "antd";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import { PlainLightPanelless } from "survey-core/themes";
import "survey-core/survey-core.css";

import { Modal, DocumentList, Space, SimpleCard } from "../../../design-system";
import type { DocumentItem } from "../../../design-system";
import {
    Project,
    ProjectSourceType,
    ProjectType,
    RfxVendor,
    allowedExtensions,
} from "../../../types/ProjectTypes";
import { createProject, getInitialSurveySchema } from "../../../common/api/ProjectClient";
import { handleError } from "../../../common/ErrorHandling";
import styles from "../ProjectView.module.less";

interface SpawnDealModalProps {
    open: boolean;
    rfxProject: Project;
    vendor: RfxVendor;
    onClose: () => void;
    onSuccess: () => void;
}

export const SpawnDealModal: FC<SpawnDealModalProps> = ({
    open,
    rfxProject,
    vendor,
    onClose,
    onSuccess,
}) => {
    const defaultName = `${rfxProject.name} — ${vendor.vendorName}`;
    const [name, setName] = useState(defaultName);
    const [documents, setDocuments] = useState<DocumentItem[]>([]);
    const [response, setResponse] = useState<Record<string, any>>({});
    const [schema, setSchema] = useState<object | null>(null);
    const [surveyModel, setSurveyModel] = useState<Model | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getInitialSurveySchema(ProjectType.DEAL, { source: ProjectSourceType.RFX })
            .then((result) => {
                if (cancelled) return;
                setSchema(result.schema);
                const m = new Model(result.schema);
                m.applyTheme(PlainLightPanelless);
                m.showCompleteButton = false;
                m.textUpdateMode = "onTyping";
                m.onValueChanged.add((s) => setResponse(s.data));
                setSurveyModel(m);
            })
            .catch(handleError);
        return () => {
            cancelled = true;
        };
    }, []);

    const handleSubmit = async () => {
        const trimmed = name.trim();
        if (!trimmed || !schema) return;
        setSubmitting(true);
        try {
            await createProject({
                type: ProjectType.DEAL,
                name: trimmed,
                source: {
                    type: ProjectSourceType.RFX,
                    rfxVendorId: vendor.id,
                },
                submitImmediately: true,
                survey: { schema, response },
                files: documents
                    .filter((d) => d.change === "added" && d.file)
                    .map((d) => d.file!),
            });
            onSuccess();
            onClose();
        } catch (err) {
            handleError(err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            title="Create deal analysis"
            width={720}
            destroyOnClose
            okText="Create"
            confirmLoading={submitting}
            okButtonProps={{ disabled: !name.trim() || !schema }}
            onOk={handleSubmit}
            onCancel={onClose}
        >
            <Space direction="vertical" size="large" fullWidth>
                <Form layout="vertical">
                    <Form.Item label="Project name" required>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={defaultName}
                        />
                    </Form.Item>
                </Form>

                <SimpleCard header="Documents" headerType="primary">
                    <DocumentList
                        documents={documents}
                        allowedExtensions={allowedExtensions}
                        onChange={setDocuments}
                    />
                </SimpleCard>

                <SimpleCard header="Context" headerType="primary">
                    <div className={styles.surveyContainer}>
                        {surveyModel ? (
                            <Survey model={surveyModel} />
                        ) : (
                            <div style={{ textAlign: "center", padding: 24 }}>
                                <Spin />
                            </div>
                        )}
                    </div>
                </SimpleCard>
            </Space>
        </Modal>
    );
};
